// Formula sheet — every formula regex-scanned (backend, zero LLM cost)
// out of quizzes the student has actually completed, grouped by subject.
import React, { useEffect, useMemo, useState } from 'react';
import { AccentRule } from './studentUi';
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { Sigma, AlertTriangle } from 'lucide-react';
import api from './studentApi';
import { getExamPrepColors } from './examPrepTheme';
import { useStudent } from './StudentApp';
import { friendlyError } from '../../lib/errors';

const UNSORTED = 'Other';

export default function FormulaSheetPage() {
  const { theme } = useStudent();
  const examPrep = getExamPrepColors(theme?.id);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/student/exam-prep/formula-sheet')
      .then(d => alive && setEntries(d.entries || []))
      .catch(e => alive && setError(friendlyError(e, 'Could not load your formula sheet.')));
    return () => { alive = false; };
  }, []);

  const bySubject = useMemo(() => {
    const groups = {};
    (entries || []).forEach(e => {
      const key = e.subject || UNSORTED;
      (groups[key] = groups[key] || []).push(e);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  return (
    <div className="space-y-5 max-w-3xl" data-testid="formula-sheet-page">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: examPrep.goldTint, color: examPrep.gold }}>
          <Sigma className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-semibold opacity-60">Exam Prep</div>
          <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>
            Formula Sheet
          </h1>
<AccentRule className="mt-2" />
          <p className="text-xs mt-1" style={{ color: 'var(--st-mute)' }}>
            Pulled automatically from quizzes you&rsquo;ve completed — take more quizzes to grow this.
          </p>
        </div>
      </header>

      {error && (
        <div className="rounded-2xl border p-4 text-sm flex items-center gap-2" style={{ borderColor: '#FCA5A5', color: '#B91C1C', background: '#FEF2F2' }}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {entries === null && !error && (
        <div className="text-sm opacity-60 py-8 text-center">Scanning your quiz history…</div>
      )}

      {entries !== null && entries.length === 0 && !error && (
        <div className="rounded-2xl border p-8 text-center" style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }} data-testid="formula-sheet-empty">
          <Sigma className="w-8 h-8 mx-auto opacity-30 mb-2" />
          <p className="text-sm" style={{ color: 'var(--st-mute)' }}>No formulas yet — complete a quiz to start building your sheet.</p>
        </div>
      )}

      {bySubject.map(([subject, items]) => (
        <section key={subject} className="space-y-2">
          <div className="text-xs font-semibold " style={{ color: examPrep.gold }}>{subject}</div>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {items.map((f, i) => (
              <div
                key={`${subject}-${i}`}
                className="rounded-2xl border px-4 py-3.5 overflow-x-auto"
                style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)', borderLeft: `3px solid ${examPrep.gold}` }}
                data-testid={`formula-card-${subject}-${i}`}>
                <BlockMath math={f.formula_latex} errorColor="#DC2626" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
